import { Injectable, inject, signal } from '@angular/core';

import { BehaviorSubject, Observable, Subject, debounceTime, filter, map, tap } from 'rxjs';

import { FirebaseService, setFirebaseOwnUserIdResolver } from '@lib/core/services/firebase.service';

import {
  ChatNotificationHandler,
  ChatNotificationPayload,
  ChatNotificationType,
  NotificationConfig,
  NotificationPermissionStatus,
  NotificationQueueItem,
} from '../models/notification.interface';
import { CHAT_LIB_CONFIG } from '../tokens/chat.tokens';
import { CHAT_NOTIFICATION_CONFIG } from '../tokens/notification.tokens';
import { ChatNavigationService } from './chat-navigation.service';

@Injectable({
  providedIn: 'root',
})
export class ChatNotificationService {
  private readonly firebaseService = inject(FirebaseService);
  private readonly navigationService = inject(ChatNavigationService);
  private readonly defaultConfig = inject(CHAT_NOTIFICATION_CONFIG);
  private readonly chatConfig = inject(CHAT_LIB_CONFIG, { optional: true });

  private readonly notificationQueue: NotificationQueueItem[] = [];
  private readonly activeNotifications: Notification[] = [];
  private readonly recentSoundTimestamps = new Map<string, number>();
  private readonly recentOwnMessageIds = new Map<string, number>();
  private notificationIdCounter = 0;

  private readonly notificationEvents$ = new Subject<ChatNotificationPayload>();
  private readonly permissionStatus$ = new BehaviorSubject<NotificationPermissionStatus>(
    (Notification.permission as NotificationPermissionStatus) || NotificationPermissionStatus.Default
  );

  readonly permissionStatus = signal<NotificationPermissionStatus>((Notification.permission as NotificationPermissionStatus) || NotificationPermissionStatus.Default);
  readonly config = signal<NotificationConfig>({
    enabled: true,
    soundEnabled: true,
    maxVisibleNotifications: 3,
    debounceMs: 1000,
    clickAction: 'navigate',
    ...this.defaultConfig,
  });

  private readonly handlers = new Map<ChatNotificationType, ChatNotificationHandler>();

  constructor() {
    this.initializeDefaultHandlers();
    this.initializeNotificationQueue();
    this.initializeFirebaseIntegration();

    setFirebaseOwnUserIdResolver(() => this.ownUserId());

    // Initialize Firebase messaging listener
    this.firebaseService.listen();
  }

  private ownUserId(): string | null {
    return this.chatConfig?.getUserInfo?.()?.userId || this.chatConfig?.ownUserInfo?.userId || null;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission(): Promise<NotificationPermissionStatus> {
    try {
      const permission = await Notification.requestPermission();
      const status = permission as NotificationPermissionStatus;

      this.permissionStatus.set(status);
      this.permissionStatus$.next(status);

      if (status === NotificationPermissionStatus.Granted) {
        this.firebaseService.requestNotificationsPermission();
      }

      return status;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return NotificationPermissionStatus.Denied;
    }
  }

  /**
   * Update notification configuration
   */
  updateConfig(newConfig: Partial<NotificationConfig>): void {
    this.config.update(current => ({ ...current, ...newConfig }));
  }

  /**
   * Register a custom handler for a specific notification type
   */
  registerHandler(handler: ChatNotificationHandler): void {
    this.handlers.set(handler.type, handler);
  }

  /**
   * Get notification events as observable
   */
  getNotificationEvents(): Observable<ChatNotificationPayload> {
    return this.notificationEvents$.asObservable();
  }

  /**
   * Get permission status as observable
   */
  getPermissionStatus(): Observable<NotificationPermissionStatus> {
    return this.permissionStatus$.asObservable();
  }

  /**
   * Manually trigger a notification (useful for testing)
   */
  triggerNotification(payload: ChatNotificationPayload): void {
    this.processNotification(payload);
  }

  /**
   * Clear all active notifications
   */
  clearAllNotifications(): void {
    this.activeNotifications.forEach(notification => {
      try {
        notification.close();
      } catch (error) {
        console.warn('Error closing notification:', error);
      }
    });
    this.activeNotifications.length = 0;
  }

  /**
   * Disable all notifications
   */
  disable(): void {
    this.updateConfig({ enabled: false });
    this.clearAllNotifications();
  }

  /**
   * Enable notifications
   */
  enable(): void {
    this.updateConfig({ enabled: true });
  }

  private initializeDefaultHandlers(): void {
    this.registerHandler({
      type: ChatNotificationType.MessageReceived,
      showSystemNotification: true,
      updateState: false,
      handler: payload => {
        if (this.config().clickAction === 'navigate' && payload.threadId) {
          // Will be handled by system notification click
        }
      },
    });

    this.registerHandler({
      type: ChatNotificationType.MessageUnread,
      showSystemNotification: false,
      updateState: true,
      handler: payload => {
        // State updates will be handled by chat state service
        this.notificationEvents$.next(payload);
      },
    });

    this.registerHandler({
      type: ChatNotificationType.MessageRead,
      showSystemNotification: false,
      updateState: true,
      handler: payload => {
        this.notificationEvents$.next(payload);
      },
    });
  }

  private initializeNotificationQueue(): void {
    // Process notification queue with debouncing
    this.notificationEvents$
      .pipe(
        filter(payload => this.config().enabled && !this.isOwnEvent(payload)),
        debounceTime(this.config().debounceMs),
        tap(payload => this.addToQueue(payload))
      )
      .subscribe(() => this.processQueue());
  }

  private initializeFirebaseIntegration(): void {
    this.firebaseService
      .getEvents()
      .pipe(
        map(event => {
          console.log('Firebase integration:', event);
          // Handle both old and new payload formats
          const data = event.data || event;

          return {
            id: data['id'] || crypto.randomUUID(),
            type: data['type'],
            date: data['date'] || new Date().toISOString(),
            threadId: data['threadId'],
            messageIds: data['messageIds'],
            // data['userId'] is the push recipient added by the backend FCM provider, NOT the sender — never read it here
            senderId: data['senderId'],
            assignees: this.resolveAssignees(data as Record<string, unknown>),
            notification: event.notification || data.notification,
            data: data,
          } as ChatNotificationPayload;
        }),
        filter(payload => Object.values(ChatNotificationType).includes(payload.type))
      )
      .subscribe(payload => this.processNotification(payload));
  }

  private processNotification(payload: ChatNotificationPayload): void {
    const handler = this.handlers.get(payload.type);

    if (!handler) {
      console.warn(`No handler registered for notification type: ${payload.type}`);
      return;
    }

    // Execute custom handler logic
    try {
      handler.handler(payload);
    } catch (error) {
      console.error(`Error in notification handler for type ${payload.type}:`, error);
    }

    // Play sound even when the system notification is suppressed (window focused),
    // but never for events produced by the current user's own actions
    if (handler.showSystemNotification && this.config().enabled && !this.isOwnEvent(payload)) {
      if (this.config().soundEnabled && this.registerSoundPlayback(payload)) this.playNotificationSound(this.config().soundUrl);
      if (this.shouldShowNotification()) this.showSystemNotification(payload);
    }

    // Emit event for state updates if needed
    if (handler.updateState) this.notificationEvents$.next(payload);
  }

  // Chat pushes are topic-broadcast to all portal clients including the sender and carry no sender id,
  // so own messages are recognized by the ids returned from this client's own send requests
  registerOwnMessage(messageId: string): void {
    const OWN_MESSAGE_TTL_MS = 60000;
    const now = Date.now();

    this.recentOwnMessageIds.forEach((timestamp, id) => {
      if (now - timestamp > OWN_MESSAGE_TTL_MS) this.recentOwnMessageIds.delete(id);
    });

    this.recentOwnMessageIds.set(messageId, now);
  }

  private isOwnEvent(payload: ChatNotificationPayload): boolean {
    const ownUserId = this.ownUserId();

    if (payload.senderId && ownUserId && payload.senderId === ownUserId) return true;

    const messageIds = [payload.id, ...(payload.messageIds?.split(',') ?? [])].map(id => id?.trim()).filter(Boolean);

    return messageIds.some(id => this.recentOwnMessageIds.has(id));
  }

  // The backend emits both `received` and `unread` events for the same message — one sound per thread within the window
  private registerSoundPlayback(payload: ChatNotificationPayload): boolean {
    const SOUND_DEDUPE_WINDOW_MS = 2000;
    const key = payload.threadId || payload.id;
    const now = Date.now();

    this.recentSoundTimestamps.forEach((timestamp, existingKey) => {
      if (now - timestamp > SOUND_DEDUPE_WINDOW_MS) this.recentSoundTimestamps.delete(existingKey);
    });

    if (this.recentSoundTimestamps.has(key)) return false;

    this.recentSoundTimestamps.set(key, now);

    return true;
  }

  // Backend assignment is single-assignee: assign replaces the list with [assigneeId], unassign clears it —
  // so the full assignee state is derivable from the push without an extra request
  private resolveAssignees(data: Record<string, unknown>): string[] | null {
    const explicit = this.parseAssignees(data['assignees']);
    const assigneeId = data['assigneeId'];

    if (explicit) return explicit;
    if (data['type'] === ChatNotificationType.ThreadAssigned && typeof assigneeId === 'string' && assigneeId) return [assigneeId];
    if (data['type'] === ChatNotificationType.ThreadUnassigned) return [];

    return null;
  }

  private parseAssignees(value: unknown): string[] | null {
    if (value == null) return null;
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string') return null;

    try {
      const parsed = JSON.parse(value);

      if (Array.isArray(parsed)) return parsed;
    } catch {
      // not JSON — fall through to comma-separated parsing
    }

    return value
      ? value
          .split(',')
          .map(id => id.trim())
          .filter(Boolean)
      : [];
  }

  private addToQueue(payload: ChatNotificationPayload): void {
    const queueItem: NotificationQueueItem = {
      id: `notification_${this.notificationIdCounter++}`,
      payload,
      timestamp: new Date(),
      attempts: 0,
      priority: this.getNotificationPriority(payload.type),
    };

    this.notificationQueue.push(queueItem);
    this.notificationQueue.sort((a, b) => b.priority - a.priority);
  }

  private processQueue(): void {
    const config = this.config();

    while (this.notificationQueue.length > 0 && this.activeNotifications.length < config.maxVisibleNotifications) {
      const item = this.notificationQueue.shift();
      if (item && this.shouldShowNotification()) {
        this.showSystemNotification(item.payload);
      }
    }
  }

  private shouldShowNotification(): boolean {
    const config = this.config();
    return (
      config.enabled && this.permissionStatus() === NotificationPermissionStatus.Granted && document.hidden // Only show when window is not focused
    );
  }

  private showSystemNotification(payload: ChatNotificationPayload): void {
    try {
      const config = this.config();

      const notification = new Notification(payload.notification?.title ?? 'У вас нове повідомлення', {
        body: payload.notification?.body,
        icon: payload.notification?.icon || config.customIcon || 'assets/images/logo.svg',
        tag: payload.threadId, // Prevent duplicate notifications for same thread
        requireInteraction: true,
      });

      // Handle click action
      notification.onclick = () => {
        window.focus();

        switch (config.clickAction) {
          case 'navigate':
            if (payload.threadId) {
              this.navigationService.navigateToThread(payload.threadId);
            }
            break;
          case 'focus':
            // Just focus window (already done above)
            break;
          case 'custom':
            // Emit event for custom handling
            this.notificationEvents$.next({
              ...payload,
              type: ChatNotificationType.MessageReceived, // Custom click event
            });
            break;
        }

        notification.close();
      };

      // Handle close/auto-close
      notification.onclose = () => {
        this.removeActiveNotification(notification);
      };

      notification.onerror = error => {
        console.error('Notification error:', error);
        this.removeActiveNotification(notification);
      };

      this.activeNotifications.push(notification);

      // Auto-close after 5 seconds if not interacted with
      setTimeout(() => {
        if (this.activeNotifications.includes(notification)) {
          notification.close();
        }
      }, 5000);
    } catch (error) {
      console.error('Error showing system notification:', error);
    }
  }

  private removeActiveNotification(notification: Notification): void {
    const index = this.activeNotifications.indexOf(notification);
    if (index > -1) {
      this.activeNotifications.splice(index, 1);
    }
  }

  private playNotificationSound(customUrl?: string): void {
    try {
      const audio = this.getOrCreateAudioElement();
      if (audio) {
        if (customUrl) {
          audio.src = customUrl;
        } else if (!audio.src && !audio.querySelector('source')) {
          audio.src = this.getDefaultSoundUrl();
        }
        audio.muted = false;
        audio.play().catch(() => {
          this.tryServiceWorkerSound();
        });
      } else {
        this.tryServiceWorkerSound();
      }
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }

  private getOrCreateAudioElement(): HTMLAudioElement | null {
    let audio = document.getElementById('notification-sound') as HTMLAudioElement;

    if (!audio) {
      audio = document.createElement('audio');
      audio.id = 'notification-sound';
      audio.preload = 'auto';
      document.body.appendChild(audio);
    }

    return audio;
  }

  private tryServiceWorkerSound(): void {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        action: 'chat:play-sound',
      });
    }
  }

  private getDefaultSoundUrl(): string {
    return '/assets/Blow.mp3';
  }

  private getNotificationPriority(type: ChatNotificationType): number {
    const priorityMap = {
      [ChatNotificationType.MessageReceived]: 10,
      [ChatNotificationType.MessageUnread]: 5,
      [ChatNotificationType.MessageRead]: 1,
      [ChatNotificationType.ThreadAssigned]: 7,
      [ChatNotificationType.ThreadUnassigned]: 3,
    };

    return priorityMap[type] || 5;
  }
}
